import React, { useEffect, useState } from "react";
import { Button } from "../design/button";
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
} from "../design/dialog";
import { PlusIcon } from "@heroicons/react/16/solid";
import { Input } from "../design/input";
import {
  Field,
  Fieldset,
  FieldGroup,
  Label,
  ErrorMessage,
} from "../design/fieldset";
import { Checkbox, CheckboxField } from "../design/checkbox";
import { useSubtask } from "../contexts/SubtaskContext";
import AssignToField from "../components/AssignToField";
import { Subtask } from "../models/Subtask";
import { Task } from "../models/Task";
import { User } from "../models/User";

interface Props {
  taskId?: Task["_id"];
  subtask?: Subtask;
  onSubtaskCreated?: (subtaskId: string) => void;
  children?: React.ReactNode;
}

interface Errors {
  title?: string;
  description?: string;
}

const SubtaskButton: React.FC<Props> = ({
  taskId,
  subtask,
  onSubtaskCreated,
  children,
}) => {
  const isEditMode = !!subtask;
  const { createSubtask, updateSubtask } = useSubtask();

  const [isOpen, setIsOpen] = useState(false);
  const [title, setTitle] = useState<string>(subtask?.title ?? "");
  const [description, setDescription] = useState<string>(
    subtask?.description ?? "",
  );
  const [assignedTo, setAssignedTo] = useState<string[]>(
    (subtask?.assignedTo as User["_id"][]) ?? [],
  );
  const [errors, setErrors] = useState<Errors>({});
  const [isSaving, setIsSaving] = useState(false);
  const [isCreatingMore, setIsCreatingMore] = useState(false);

  useEffect(() => {
    if (isEditMode && subtask) {
      setTitle(subtask.title);
      setDescription(subtask.description ?? "");
      setAssignedTo((subtask.assignedTo as User["_id"][]) ?? []);
    }
  }, [isOpen, subtask]);

  const validateForm = () => {
    const newErrors: Errors = {};
    if (!title.trim()) newErrors.title = "A title is required.";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsSaving(true);

    try {
      if (isEditMode && subtask) {
        await updateSubtask(subtask._id, {
          title,
          description,
          assignedTo,
        });
        setIsOpen(false);
      } else {
        const newSubtaskId = await createSubtask({
          title,
          description,
          status: "pending",
          task: taskId!,
          assignedTo,
        });
        if (onSubtaskCreated) onSubtaskCreated(newSubtaskId);
        if (!isCreatingMore) setIsOpen(false);
        resetForm();
      }
    } catch (e) {
      console.error("Error submitting subtask:", e);
    } finally {
      setIsSaving(false);
    }
  };

  const resetForm = () => {
    setTitle("");
    setDescription("");
    setAssignedTo([]);
    setErrors({});
  };

  return (
    <>
      <Button type="button" onClick={() => setIsOpen(true)}>
        {children ?? (
          <div className="flex items-center gap-2">
            {!isEditMode && <PlusIcon className="w-4 h-4" />}
            <span className="hidden sm:block">
              {isEditMode ? "Edit Subtask" : "Create Subtask"}
            </span>
          </div>
        )}
      </Button>
      <Dialog open={isOpen} onClose={setIsOpen}>
        <DialogTitle>
          {isEditMode ? "Edit Subtask" : "Create Subtask"}
        </DialogTitle>
        <DialogBody>
          <Fieldset>
            <FieldGroup>
              <Field>
                <Label>Title</Label>
                <Input
                  type="text"
                  value={title}
                  placeholder="Subtask Title"
                  onChange={(e) => setTitle(e.target.value)}
                  disabled={isSaving}
                />
                {errors.title && <ErrorMessage>{errors.title}</ErrorMessage>}
              </Field>

              <Field>
                <Label>Description</Label>
                <Input
                  type="text"
                  value={description}
                  placeholder="Subtask Description"
                  onChange={(e) => setDescription(e.target.value)}
                  disabled={isSaving}
                />
                {errors.description && (
                  <ErrorMessage>{errors.description}</ErrorMessage>
                )}
              </Field>

              <AssignToField
                value={assignedTo}
                onChange={setAssignedTo}
                disabled={isSaving}
              />
            </FieldGroup>
          </Fieldset>
        </DialogBody>
        <DialogActions className="justify-between">
          {!isEditMode && (
            <CheckboxField>
              <Checkbox
                checked={isCreatingMore}
                onChange={(checked) => setIsCreatingMore(checked)}
              />
              <Label>Create more</Label>
            </CheckboxField>
          )}
          <div className="flex gap-2">
            <Button plain onClick={() => setIsOpen(false)} disabled={isSaving}>
              Cancel
            </Button>
            <Button disabled={isSaving} onClick={handleSubmit}>
              {isSaving ? "Saving..." : "Save"}
            </Button>
          </div>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SubtaskButton;
